/**
 * Same-origin API client. nginx fronts both web + API, so API_BASE is empty.
 *
 * The session rides an HttpOnly cookie (TNK-54) that the browser attaches to
 * every same-origin request automatically — there is no Authorization header
 * and the token is never visible to JS. Mutating requests echo the readable
 * CSRF cookie back in X-CSRF-Token (double-submit; the server compares the two).
 */
export const API_BASE = "";

const CSRF_COOKIE = "tcg-web.csrf";
const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

// Drop the pre-TNK-54 localStorage session token — no longer read or sent, so
// remove it to kill the XSS target. Also strip the guest flag in production:
// guest login is a dev-only bypass (see auth.tsx), so a prod build must never
// honor a lingering flag; dev keeps it so the bypass persists across reloads.
try {
  window.localStorage.removeItem("tcg-web.session-token");
  if (!import.meta.env.DEV) window.localStorage.removeItem("tcg-web.guest");
} catch {
  // localStorage unavailable (SSR / private mode) — nothing to clean up.
}

function readCsrfToken(): string {
  try {
    const prefix = `${CSRF_COOKIE}=`;
    const row = document.cookie.split("; ").find((r) => r.startsWith(prefix));
    return row ? decodeURIComponent(row.slice(prefix.length)) : "";
  } catch {
    return "";
  }
}

let _onUnauthorized: (() => void) | null = null;

export function setOnUnauthorized(fn: (() => void) | null): void {
  _onUnauthorized = fn;
}

export function notifyUnauthorized(): void {
  _onUnauthorized?.();
}

export async function apiFetch(
  url: string,
  init?: RequestInit,
): Promise<Response> {
  const headers = new Headers(init?.headers);
  const method = (init?.method ?? "GET").toUpperCase();
  if (!SAFE_METHODS.has(method)) {
    const csrf = readCsrfToken();
    if (csrf) headers.set("X-CSRF-Token", csrf);
  }
  const res = await fetch(url, { ...init, headers });
  if (res.status === 401) notifyUnauthorized();
  return res;
}
